import { useMemo, useState } from "react";

import type { AgCartesianChartOptions, AgChartOptions } from "ag-charts-community";
import { Bot, Users } from "lucide-react";

import { AgChartPanel, buildAgOptions, resolveToken, useIsDark } from "@/components/cinch/charts";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCan } from "@/lib/auth";
import { MarkdownView } from "@/lib/markdown";

import type { CashFlowNote, CashFlowSummary, CashFlowWeek } from "../api";
import {
  collectionsDataset,
  DEFAULT_CASH_THRESHOLD,
  endingCashDataset,
  fmtUsd,
  runwayDataset,
  weekLabel,
} from "../lib";
import { CashFlowNotesArea } from "./CashFlowNotesArea";
import { GenerateAiDialog } from "./GenerateAiDialog";

/** Read-only rendering of an AI note with its provenance line. Humans don't
 *  edit these — they regenerate them (or the scheduled backfill writes them
 *  five days after a week closes). */
function AiNoteView({ note }: { note: CashFlowNote | undefined }) {
  if (!note) {
    return (
      <p className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
        No AI note yet. Generate one, or it will be written automatically five days after the
        week closes.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
        <MarkdownView source={note.body} />
      </div>
      <p className="text-xs text-muted-foreground">
        Generated by <b className="text-foreground">{note.llm_meta?.model ?? "AI"}</b>
        {note.llm_meta?.trigger === "scheduled" ? " (scheduled)" : ""} ·{" "}
        {new Date(note.updated_at).toLocaleString()}
      </p>
    </div>
  );
}

/** Dash the projected series (forecast/extension) like the 13W report does,
 *  and relabel the reference cross-line. buildAgOptions covers the standard
 *  styling; these are the cash-flow-specific touches. */
function patchOptions(
  options: AgChartOptions,
  dashedKeys: string[],
  referenceText?: string,
): AgChartOptions {
  const opts = options as AgCartesianChartOptions;
  const series = (opts.series ?? []).map((s) => {
    const so = s as unknown as Record<string, unknown>;
    if (so.type === "line" && dashedKeys.includes(String(so.yKey))) {
      return { ...so, lineDash: [6, 4], marker: { ...(so.marker as object), size: 4 } };
    }
    return so;
  });
  const axes = opts.axes as { y?: { crossLines?: { label?: { text?: string } }[] } } | undefined;
  if (referenceText && axes?.y?.crossLines?.[0]?.label) {
    axes.y.crossLines[0].label.text = referenceText;
  }
  return { ...opts, series } as unknown as AgChartOptions;
}

/** The runway chart's threshold lines: a SOLID red line at zero cash and a
 *  DASHED red line at the configured cash budget (Admin tab). */
function withRunwayCrossLines(options: AgChartOptions, cashThreshold: number): AgChartOptions {
  const danger = resolveToken("danger");
  const opts = options as AgCartesianChartOptions;
  const yAxis = (opts.axes as { y?: Record<string, unknown> } | undefined)?.y ?? {};
  yAxis.crossLines = [
    {
      type: "line",
      value: 0,
      stroke: danger,
      strokeWidth: 2,
      label: {
        enabled: true,
        text: "Zero cash",
        position: "top-left",
        fontSize: 10,
        color: danger,
      },
    },
    {
      type: "line",
      value: cashThreshold,
      stroke: danger,
      strokeWidth: 1.5,
      lineDash: [6, 4],
      label: {
        enabled: true,
        text: `Cash budget: ${fmtUsd(cashThreshold)}`,
        position: "top-right",
        fontSize: 10,
        color: danger,
      },
    },
  ];
  return { ...opts, axes: { ...(opts.axes as object), y: yAxis } } as unknown as AgChartOptions;
}

function noteOf(
  notes: CashFlowNote[],
  kind: "ai" | "human",
  weekEnding: string | null,
): CashFlowNote | undefined {
  return notes.find((n) => n.kind === kind && n.week_ending === weekEnding);
}

const SEGMENT_BADGE: Record<
  CashFlowWeek["segment"],
  { label: string; variant: "success" | "info" | "neutral" }
> = {
  actual: { label: "Actual", variant: "success" },
  forecast: { label: "Forecast", variant: "info" },
  extension: { label: "Extension", variant: "neutral" },
};

export function SummaryTab({ summary }: { summary: CashFlowSummary }) {
  const isDark = useIsDark();
  const canWrite = useCan("cash_flow.write");
  const { run, weeks, months, notes } = summary;
  // Optional-chained so a stale cached payload (pre-settings shape) degrades
  // to the default instead of crashing the tab.
  const cashThreshold = summary.settings?.cash_threshold ?? DEFAULT_CASH_THRESHOLD;

  const endingCash = useMemo(
    () =>
      patchOptions(
        buildAgOptions(endingCashDataset(weeks, cashThreshold), "line", isDark),
        ["forecast"],
        `Cash budget: ${fmtUsd(cashThreshold)}`,
      ),
    [weeks, cashThreshold, isDark],
  );
  const collections = useMemo(
    () => buildAgOptions(collectionsDataset(months), "stacked-bar", isDark),
    [months, isDark],
  );
  const runway = useMemo(
    () =>
      withRunwayCrossLines(
        patchOptions(buildAgOptions(runwayDataset(weeks), "line", isDark), [
          "forecast",
          "extension",
        ]),
        cashThreshold,
      ),
    [weeks, cashThreshold, isDark],
  );

  // Week-by-week review: closed + model-horizon weeks (extension weeks are
  // extrapolation — nothing to review against yet).
  const reviewableWeeks = useMemo(
    () => weeks.filter((w) => w.segment !== "extension"),
    [weeks],
  );
  const [reviewWeek, setReviewWeek] = useState<string | null>(null);
  const selectedWeek =
    reviewableWeeks.find((w) => w.week_ending === reviewWeek) ??
    [...reviewableWeeks].reverse().find((w) => w.segment === "actual") ??
    reviewableWeeks[0];

  if (!run) {
    return (
      <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
        No forecast run loaded yet. Upload a bank statement / forecast model to get started.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {(run.warnings ?? []).length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning-light p-4 text-sm">
          <p className="mb-1 font-medium">
            Assumptions need extending — later weeks understate cash
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            {(run.warnings ?? []).map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground">
            A driver with nothing scheduled contributes zero, not a repeat of last month — so
            an out-of-date assumption reads as good news. These are the warnings as they stood
            when this run was published.
          </p>
        </div>
      )}
      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Ending Cash by Week — Model Horizon</CardTitle>
            <CardDescription>
              Bank-tied actuals through {run.actuals_through ? weekLabel(run.actuals_through) : "—"},
              then the 13-week model
              {run.collections_run_rate
                ? ` at ${fmtUsd(run.collections_run_rate)}/mo collections`
                : ""}
              .
              {run.complete_through
                ? ` Fully driven through ${weekLabel(run.complete_through)}; after that some assumptions have no scheduled amount.`
                : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AgChartPanel options={endingCash} className="h-72" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Customer Collections by Month</CardTitle>
            <CardDescription>
              Bank-tied collections by the month each week ends in — the workbook's rule, so a
              week spanning month end counts once, in the month it closes. The open month's
              remaining weeks are the model's forecast for them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AgChartPanel options={collections} className="h-72" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Runway Extension</CardTitle>
          <CardDescription>
            The same run rate, payroll schedule, and vendor cadence carried past the model
            horizon — extrapolation, not model output. Solid red is zero cash; dashed red is
            the {fmtUsd(cashThreshold)} cash budget (set on the Admin tab).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AgChartPanel options={runway} className="h-[28rem]" />
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-muted-foreground" />
                  AI assumptions &amp; comments
                </CardTitle>
                <CardDescription className="mt-1.5">
                  Written by the model from the run's numbers and the team's comments — not
                  hand-edited. Regenerate any time with the LLM of your choice.
                </CardDescription>
              </div>
              {canWrite && <GenerateAiDialog runId={run.id} weekEnding={null} />}
            </div>
          </CardHeader>
          <CardContent>
            <AiNoteView note={noteOf(notes, "ai", null)} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              Team comments
            </CardTitle>
            <CardDescription>
              Finance-team commentary on this run. Saved to the cash flow database so it feeds
              future forecast analysis.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CashFlowNotesArea
              key={`human-${run.id}`}
              runId={run.id}
              weekEnding={null}
              kind="human"
              initialBody={noteOf(notes, "human", null)?.body ?? ""}
              label=""
              placeholder="Context the model can't see — confirmed billing weeks, one-off payments, timing calls…"
              readOnly={!canWrite}
              minHeightPx={120}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <CardTitle>Week-by-week review</CardTitle>
              <CardDescription>
                How each week landed against the forecast, and why — the finance team records
                the reasons; the AI drafts its analysis on demand, or automatically five days
                after the week closes.
              </CardDescription>
            </div>
            <Select
              value={selectedWeek?.week_ending ?? ""}
              onValueChange={(v) => setReviewWeek(v)}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Pick a week" />
              </SelectTrigger>
              <SelectContent>
                {reviewableWeeks.map((w) => (
                  <SelectItem key={w.week_ending} value={w.week_ending}>
                    WE {weekLabel(w.week_ending, true)} · {SEGMENT_BADGE[w.segment].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        {selectedWeek && (
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
              <span className="font-semibold">
                Week ending {weekLabel(selectedWeek.week_ending, true)}
              </span>
              <Badge variant={SEGMENT_BADGE[selectedWeek.segment].variant}>
                {SEGMENT_BADGE[selectedWeek.segment].label}
              </Badge>
              <span>
                Inflows <span className="font-medium">{fmtUsd(selectedWeek.total_inflows)}</span>
              </span>
              <span>
                Outflows <span className="font-medium">{fmtUsd(selectedWeek.total_outflows)}</span>
              </span>
              <span>
                Net{" "}
                <span
                  className={
                    selectedWeek.net_change < 0 ? "font-medium text-danger" : "font-medium text-success"
                  }
                >
                  {fmtUsd(selectedWeek.net_change)}
                </span>
              </span>
              <span>
                Ending cash <span className="font-medium">{fmtUsd(selectedWeek.ending_cash)}</span>
              </span>
            </div>
            <div className="grid gap-4 xl:grid-cols-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium">AI analysis</span>
                  {canWrite && (
                    <GenerateAiDialog runId={run.id} weekEnding={selectedWeek.week_ending} />
                  )}
                </div>
                <AiNoteView note={noteOf(notes, "ai", selectedWeek.week_ending)} />
              </div>
              <CashFlowNotesArea
                key={`human-${run.id}-${selectedWeek.week_ending}`}
                runId={run.id}
                weekEnding={selectedWeek.week_ending}
                kind="human"
                initialBody={noteOf(notes, "human", selectedWeek.week_ending)?.body ?? ""}
                label="Finance team reasons"
                placeholder="Why did this week land ahead of / behind the forecast?"
                readOnly={!canWrite}
              />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
